// Shared resolvers that map a trip / lead (+ agent) into the WhatsApp template
// variable context consumed by <SendWhatsAppDialog />. Keeps the auto-fill
// identical across desktop (EditQuote / Leads) and mobile (MobileTripDetail /
// MobileLeadDetail) so a template renders the same everywhere.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const inr = (n) => (n == null || n === '' ? '' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);

// Clean, ref-first public links from a linked itinerary (no internal ids).
export const itinLinks = (itin) => {
  const ref = itin?.public_ref || itin?.id;
  let base = (typeof window !== 'undefined') ? window.location.origin : '';
  try { if (itin?.public_url) base = new URL(itin.public_url).origin; } catch (_) { /* noop */ }
  return {
    preview_link: ref ? `${base}/${ref}/preview` : '',
    voucher_link: ref ? `${base}/${ref}/voucher` : '',
  };
};

export const buildTripWaContext = (trip, { itinerary, user, outstanding, paymentLink } = {}) => {
  const links = itinLinks(itinerary);
  return {
    client_name: (trip?.guestName && trip.guestName !== '(Pending guest)') ? trip.guestName : '',
    destination: trip?.destination || '',
    departure_date: fmtDate(trip?.travelStartDate),
    outstanding_amount: inr(outstanding),
    booking_ref: trip?.displayTripId || trip?.tripId || '',
    payment_link: paymentLink || '',
    preview_link: links.preview_link,
    voucher_link: links.voucher_link,
    agent_name: user?.name || '',
    agent_phone: user?.phone || '',
  };
};

export const tripSuggestHint = (trip, outstanding) => {
  const status = (trip?.status || '').toLowerCase();
  if (trip?.travelStartDate) {
    const d = new Date(trip.travelStartDate);
    if (!isNaN(d.getTime())) {
      const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
      if (days >= 0 && days <= 2) return 'departure';
    }
  }
  if (Number(outstanding) > 0 && ['confirmed', 'accepted', 'ongoing', 'completed'].includes(status)) return 'payment';
  if (['sent', 'draft'].includes(status)) return 'quote';
  return null;
};

export const buildLeadWaContext = (lead, { user } = {}) => ({
  client_name: lead?.name || '',
  destination: lead?.destination || '',
  departure_date: fmtDate(lead?.travelDate),
  booking_ref: lead?.leadId || '',
  agent_name: user?.name || '',
  agent_phone: user?.phone || '',
});

export const leadSuggestHint = (lead) => {
  const st = (lead?.status || 'new').toLowerCase();
  if (['qualified', 'quoted'].includes(st)) return 'quote';
  if (['new', 'contacted'].includes(st)) return 'enquiry';
  return null;
};

// Best-effort: find this trip's active payment link → branded /pay URL.
export const resolveTripPaymentLink = async (tripId) => {
  if (!tripId) return '';
  try {
    const r = await axios.get(`${BACKEND_URL}/api/payments/links?limit=100`, { withCredentials: true });
    const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
    const match = items.find((p) => p.tripId === tripId && (p.status || '').toLowerCase() !== 'cancelled');
    if (!match) return '';
    if (match.short_url) return match.short_url;
    const ref = match.payment_ref || match.token;
    return ref ? `${window.location.origin}/pay/${ref}` : '';
  } catch { return ''; }
};
